import { AlertTriangle, Bell, Building2, CheckCircle2, Clock3, RadioTower, RefreshCw, Send, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { ProblemList } from "../components/ProblemList";
import { Button, Card, DataAge, ErrorState, LoadingBlock, PageHeader } from "../components/ui";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useRemote } from "../hooks/useRemote";
import { apiRequest } from "../lib/api";
import { percentage } from "../lib/format";
import type { OverviewResponse } from "../types";

export function OverviewPage() {
  const remote = useRemote(() => apiRequest<OverviewResponse>("/api/v1/overview"), [], 30_000);
  useDocumentTitle("Resumen general");

  return (
    <div className="page">
      <PageHeader
        eyebrow="Operación en tiempo real"
        title="Resumen general"
        description="Un panorama claro del estado de todos los edificios."
        actions={<Button variant="secondary" size="small" loading={remote.refreshing} onClick={remote.reload}><RefreshCw size={15} /> Actualizar</Button>}
      />
      {remote.loading && !remote.data ? <LoadingBlock rows={6} /> : null}
      {remote.error && !remote.data ? <ErrorState error={remote.error} onRetry={remote.reload} /> : null}
      {remote.data ? <OverviewContent data={remote.data} /> : null}
    </div>
  );
}

function OverviewContent({ data }: { data: OverviewResponse }) {
  const total = Object.values(data.totals).reduce((sum, value) => sum + value, 0);
  const operationalPercent = percentage(data.totals.operational, total);

  return (
    <>
      <section className="summary-grid" aria-label="Estado de los edificios">
        <Card className="summary-card summary-card--outage">
          <span className="summary-card__icon"><AlertTriangle size={21} /></span>
          <span className="summary-card__label">Fuera de servicio</span>
          <strong>{data.totals.outage}</strong>
          <Link to="/edificios?status=outage">Ver edificios</Link>
        </Card>
        <Card className="summary-card summary-card--degraded">
          <span className="summary-card__icon"><Bell size={21} /></span>
          <span className="summary-card__label">Con problemas</span>
          <strong>{data.totals.degraded}</strong>
          <Link to="/edificios?status=degraded">Ver edificios</Link>
        </Card>
        <Card className="summary-card summary-card--stale">
          <span className="summary-card__icon"><Clock3 size={21} /></span>
          <span className="summary-card__label">Sin datos recientes</span>
          <strong>{data.totals.stale}</strong>
          <Link to="/edificios?status=stale">Revisar monitoreo</Link>
        </Card>
        <Card className="summary-card summary-card--operational">
          <span className="summary-card__icon"><CheckCircle2 size={21} /></span>
          <span className="summary-card__label">Operativos</span>
          <strong>{data.totals.operational}</strong>
          <span>{operationalPercent}% del total</span>
        </Card>
      </section>

      <section className="overview-layout">
        <Card className="attention-card">
          <div className="card-heading">
            <div><p className="eyebrow">Prioridad operativa</p><h2>Atención ahora</h2></div>
            <Link className="text-link" to="/problemas">Ver todos los problemas</Link>
          </div>
          <ProblemList problems={data.attention.slice(0, 8)} compact />
        </Card>

        <aside className="overview-side">
          <Card className="portfolio-card">
            <div className="card-heading"><div><p className="eyebrow">Cobertura</p><h2>Estado del portfolio</h2></div><Building2 size={22} /></div>
            <div className="portfolio-score"><strong>{operationalPercent}%</strong><span>edificios operativos</span></div>
            <div className="portfolio-bar" aria-label={`${operationalPercent}% de edificios operativos`}><span style={{ width: `${operationalPercent}%` }} /></div>
            <dl className="compact-stats">
              <div><dt>Total monitoreado</dt><dd>{total}</dd></div>
              <div><dt>En mantenimiento</dt><dd><Wrench size={14} /> {data.totals.maintenance}</dd></div>
            </dl>
          </Card>
          <Card>
            <div className="card-heading"><div><p className="eyebrow">Servicios</p><h2>Salud del sistema</h2></div><RadioTower size={22} /></div>
            <div className="service-health">
              <div className={`service-health__item service-health__item--${data.lastSynchronization.status}`}>
                <span className="service-health__icon"><RadioTower size={18} /></span>
                <div><strong>Sincronización</strong><DataAge value={data.lastSynchronization.at} /></div>
              </div>
              <div className={`service-health__item service-health__item--${data.deliveryHealth.status}`}>
                <span className="service-health__icon"><Send size={18} /></span>
                <div><strong>Envío de avisos</strong><span>{data.deliveryHealth.failedLast24h === 0 ? "Sin fallas en 24 horas" : `${data.deliveryHealth.failedLast24h} fallas en 24 horas`}</span></div>
              </div>
            </div>
          </Card>
        </aside>
      </section>
    </>
  );
}
